import { useTranslation } from "react-i18next";

/** One provenance fact as the harness recorded it on the golden and reads it now. */
type Pair = { golden?: unknown; current?: unknown };

export type EnvironmentFact =
  | { kind: "displayOs" | "capture" | "other"; name: string; golden: string; current: string }
  | { kind: "layout"; golden: string; current: string }
  | { kind: "layoutFile" }
  | { kind: "fonts" };

/** "…version(331)…" inside a pdc provenance line, as "v331". */
function layoutVersion(v: unknown): string | null {
  const m = /version\((\d+)\)/.exec(String(v ?? ""));
  return m ? `v${m[1]}` : null;
}

const text = (v: unknown) => (v === undefined || v === null || v === "" ? "—" : String(v));

/** The facts that differ, in the order a reader asks about them: the build,
    the layout, the fonts, how the picture was taken, then anything else. */
export function environmentFacts(diff: Record<string, Pair>): EnvironmentFact[] {
  const first = ["display_os", "pdc", "fonts_md5", "capture"];
  const keys = [
    ...first.filter((k) => k in diff),
    ...Object.keys(diff).filter((k) => !first.includes(k)).sort(),
  ];
  return keys.map((key) => {
    const pair = diff[key] && typeof diff[key] === "object" ? diff[key] : {};
    const golden = text(pair.golden);
    const current = text(pair.current);
    if (key === "display_os") return { kind: "displayOs", name: key, golden, current };
    if (key === "pdc") {
      const g = layoutVersion(pair.golden);
      const c = layoutVersion(pair.current);
      return g && c ? { kind: "layout", golden: g, current: c } : { kind: "layoutFile" };
    }
    if (key === "fonts_md5") return { kind: "fonts" };
    if (key === "capture") return { kind: "capture", name: key, golden, current };
    return { kind: "other", name: key, golden, current };
  });
}

/** Says, in one sentence, when the golden was not recorded on the build,
    layout or fonts this run has - the harness's `environment_diff` on a
    compare step, which used to sit unread under "Golden recorded from" as
    raw key/value rows. A different DisplayOS or layout explains a red golden
    before anybody opens the pictures; it does not excuse it, so the verdict
    above stays what it is. */
export default function GoldenEnvironment({ body }: { body: Record<string, unknown> }) {
  const { t } = useTranslation();
  const diff =
    body.environment_diff && typeof body.environment_diff === "object" && !Array.isArray(body.environment_diff)
      ? (body.environment_diff as Record<string, Pair>)
      : null;
  const warning = typeof body.environment_warning === "string" ? body.environment_warning : "";
  const clauses = (diff ? environmentFacts(diff) : []).map((f) => {
    switch (f.kind) {
      case "displayOs":
        return t("runDetail.goldenElsewhereDisplayOs", { golden: f.golden, current: f.current });
      case "layout":
        return t("runDetail.goldenElsewhereLayout", { golden: f.golden, current: f.current });
      case "layoutFile":
        return t("runDetail.goldenElsewhereLayoutFile");
      case "fonts":
        return t("runDetail.goldenElsewhereFonts");
      case "capture":
        return t("runDetail.goldenElsewhereCapture", { golden: f.golden, current: f.current });
      default:
        return t("runDetail.goldenElsewhereOther", { key: f.name, golden: f.golden, current: f.current });
    }
  });
  if (!clauses.length && !warning) return null;
  return (
    <div className="text-[11px] text-amber-700 dark:text-amber-400" data-testid="golden-environment">
      {clauses.length > 0 && <p>{t("runDetail.goldenElsewhere", { list: clauses.join("; ") })}</p>}
      {warning && <p>{t("runDetail.goldenElsewhereUnknown", { detail: warning })}</p>}
    </div>
  );
}
